import { CalendarPlus, ChevronLeft, ChevronRight, FileDown, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { instructorReservations } from '@/api/endpoints'
import DateInput from '@/components/ui/DateInput'
import { Alert, Spinner } from '@/components/ui'
import ContextMenu, { type MenuSection } from '@/components/ui/ContextMenu'
import { CenterFilter, useCenterFilter, useCenters } from '@/features/centers/useCenters'
import { useBootstrap } from '@/hooks/useBootstrap'
import PaymentDetailsModal from '@/features/pricing/PaymentDetailsModal'
import { addDays, startOfWeek, timeToSlot, toISODate } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import {
  PaymentType,
  ReserveStatus,
  type ClientReservation,
  type InstructorReservation,
} from '@/types/api'

import CalendarGrid from './CalendarGrid'
import ClientReservationModal from './ClientReservationModal'
import InstructorReservationModal from './InstructorReservationModal'
import UndoRedoControl from './UndoRedoControl'
import { OnboardingCard } from '@/features/onboarding/OnboardingCard'
import {
  useCalendar,
  useCalendarSync,
  useDeleteClientReservation,
  useDeleteInstructorReservation,
  useFillFavorites,
  useMoveClientReservation,
  useSetClientReservationPayment,
  useSetClientReservationStatus,
  useSetInstructorReservationStatus,
} from './useCalendar'

type SortMode = 'courses' | 'name' | 'priority' | 'center'

export interface ClipboardEntry {
  reservation: ClientReservation
  mode: 'move' | 'copy'
}

export default function CalendarPage() {
  const { t } = useTranslation()
  const user = useAuth((s) => s.user)
  const readOnly = !user?.can_write
  // Prezerajúci len pozerá: žiadne menu po kliknutí, žiadne okno rezervácie
  // po dvojkliku, žiadne presúvanie (server mu aj tak nič iné nedá).
  const viewOnly = user?.role === 'viewer'
  const noop = () => {}

  // `navigate` is already taken by the week stepper below.
  const goTo = useNavigate()
  const [params, setParams] = useSearchParams()
  const days = Number.parseInt(params.get('days') ?? '7', 10) || 7
  const start = params.get('start') ?? toISODate(days === 7 ? startOfWeek(new Date()) : new Date())
  const [sort, setSort] = useState<SortMode>(
    () => (localStorage.getItem('l2s:sortInsRes') as SortMode) ?? 'courses',
  )

  // Filter stredísk (`sql/040`) - spoločný s platbami a štatistikami.
  const [centerIds] = useCenterFilter()
  const { data, isLoading, isFetching, error } = useCalendar(start, days, centerIds)
  // Zmenu, ktorú urobil niekto iný, netreba hľadať prekliknutím - kalendár
  // si ju natiahne sám (`useCalendarSync`).
  useCalendarSync()
  const { data: boot } = useBootstrap()
  const fillFavorites = useFillFavorites()
  const moveReservation = useMoveClientReservation()
  const setLessonStatus = useSetClientReservationStatus()
  const deleteLesson = useDeleteClientReservation()
  const setLessonPayment = useSetClientReservationPayment()
  const setBlockStatus = useSetInstructorReservationStatus()
  const deleteBlock = useDeleteInstructorReservation()

  const [instructorModal, setInstructorModal] = useState<
    { reservation: InstructorReservation | null; day: string } | null
  >(null)
  const [clientModal, setClientModal] = useState<
    { reservation: ClientReservation | null; day: string; part: number; blockId: number | null } | null
  >(null)
  const [clipboard, setClipboard] = useState<ClipboardEntry | null>(null)
  const [menu, setMenu] = useState<
    | { kind: 'lesson'; lesson: ClientReservation; x: number; y: number }
    | { kind: 'instructor'; block: InstructorReservation; x: number; y: number }
    | null
  >(null)
  const [payments, setPayments] = useState<ClientReservation | null>(null)

  function navigate(delta: number) {
    const next = addDays(new Date(start), delta * days)
    setParams({ start: toISODate(next), days: String(days) })
  }

  function setDays(value: number) {
    setParams({ start, days: String(value) })
  }

  function changeSort(value: SortMode) {
    setSort(value)
    localStorage.setItem('l2s:sortInsRes', value)
  }

  // Poradie stredísk z Nastavení (`bootstrap.centers` chodí zoradené).
  const { centers: allCenters, multi: multiCenter } = useCenters()
  // Zoradenie podľa strediska má zmysel len pri viacerých strediskách.
  const effectiveSort: SortMode = sort === 'center' && !multiCenter ? 'courses' : sort

  const sortedBlocks = useMemo(() => {
    const blocks = data?.instructor_reservations ?? []
    const copy = [...blocks]
    const byName = (a: InstructorReservation, b: InstructorReservation) =>
      (a.instructor?.full_name ?? '').localeCompare(b.instructor?.full_name ?? '', 'sk')
    const centerRank = (id: number | undefined) => {
      const index = allCenters.findIndex((c) => c.id === id)
      return index === -1 ? Number.MAX_SAFE_INTEGER : index
    }
    const sort = effectiveSort
    if (sort === 'center') {
      copy.sort((a, b) => centerRank(a.center_id) - centerRank(b.center_id) || byName(a, b))
    } else if (sort === 'name') {
      copy.sort((a, b) =>
        (a.instructor?.full_name ?? '').localeCompare(b.instructor?.full_name ?? '', 'sk'),
      )
    } else if (sort === 'priority') {
      copy.sort((a, b) => (b.instructor?.priority ?? 0) - (a.instructor?.priority ?? 0))
    } else {
      // "courses": group by the course set, then by name - the legacy default
      copy.sort((a, b) => {
        const byCourse = (a.idscourses ?? '').localeCompare(b.idscourses ?? '')
        if (byCourse !== 0) return byCourse
        return (a.instructor?.full_name ?? '').localeCompare(b.instructor?.full_name ?? '', 'sk')
      })
    }
    return copy
  }, [data?.instructor_reservations, effectiveSort, allCenters])

  /** Double click - straight to the editor. */
  function openLessonDetail(lesson: ClientReservation) {
    setClientModal({
      reservation: lesson,
      day: lesson.start?.slice(0, 10) ?? start,
      part: timeToSlot(lesson.start, data?.grid.start_hour ?? 9, data?.grid.interval ?? 0.5) ?? 1,
      blockId: lesson.instructor_reservation_id,
    })
  }

  /**
   * Single click - the action menu.
   *
   * Laid out like the legacy screen: payment state on top (each method in its
   * own colour), then the actions, then the reservation status with a tick on
   * the current one.
   */
  function lessonMenu(lesson: ClientReservation): MenuSection[] {
    const paid = lesson.client_paid !== PaymentType.Unpaid
    const payments: Array<{ value: number; className: string }> = [
      { value: PaymentType.Cash, className: 'text-green-700' },
      { value: PaymentType.Card, className: 'text-action-600' },
      { value: PaymentType.Transfer, className: 'text-cyan-700' },
      { value: PaymentType.Invoice, className: 'text-brand-600' },
      { value: PaymentType.Other, className: 'text-amber-600' },
      { value: PaymentType.Unpaid, className: 'text-slate-400' },
    ]

    const sections: MenuSection[] = [
      {
        label: t('calendar.menuPaid'),
        items: payments.map((p) => ({
          key: `pay-${p.value}`,
          label: t(`reservation.payment.${p.value}`),
          className: p.className,
          checked: lesson.client_paid === p.value,
          disabled: readOnly,
          onSelect: () => setLessonPayment.mutate({ id: lesson.id, clientPaid: p.value }),
        })),
      },
      // Editing the lesson and moving it around are the same kind of act -
      // they change the booking. Money is a different kind of act, so it gets
      // its own group rather than sitting between "copy" and "delete".
      {
        items: [
          {
            key: 'edit',
            label: t('calendar.menuEdit'),
            onSelect: () => openLessonDetail(lesson),
          },
          ...(readOnly
            ? []
            : [
                // Lekcia na doklade sa presunúť nedá - dôvod je v title na
                // dlaždici (`calendar.lockedByReceipt`). Uhradená áno.
                ...(lesson.has_issued_receipt
                  ? []
                  : [
                      {
                        key: 'move',
                        label: t('calendar.menuMove'),
                        onSelect: () => setClipboard({ reservation: lesson, mode: 'move' }),
                      },
                    ]),
                {
                  key: 'copy',
                  label: t('calendar.menuCopy'),
                  onSelect: () => setClipboard({ reservation: lesson, mode: 'copy' }),
                },
              ]),
        ],
      },
      {
        items: [
          {
            key: 'payments',
            label: t('payments.open'),
            onSelect: () => setPayments(lesson),
          },
          ...(!readOnly && boot?.settings.module_cash_receipt
            ? [
                {
                  key: 'receipt',
                  label: t('cashReceipts.issue'),
                  onSelect: () => {
                    window.location.href = `/cash-receipts?client_reservation_id=${lesson.id}`
                  },
                },
              ]
            : []),
        ],
      },
    ]

    if (!readOnly) {
      sections.push({
        items: [
          {
            key: 'confirmed',
            label: t('calendar.menuConfirmed'),
            checked: lesson.status_id === ReserveStatus.Confirmed,
            onSelect: () =>
              setLessonStatus.mutate({ id: lesson.id, statusId: ReserveStatus.Confirmed }),
          },
          {
            key: 'preliminary',
            label: t('calendar.menuPreliminary'),
            checked: lesson.status_id === ReserveStatus.Preliminary,
            onSelect: () =>
              setLessonStatus.mutate({ id: lesson.id, statusId: ReserveStatus.Preliminary }),
          },
          {
            key: 'cancelled',
            label: t('calendar.menuCancelled'),
            checked: lesson.status_id === ReserveStatus.Cancelled,
            onSelect: () => {
              // Uhradená: po zrušení treba peniaze vrátiť alebo pripísať na
              // kredit - pripomenie to skôr, než sa na to zabudne.
              if (paid && !window.confirm(t('reservation.cancelPaidConfirm'))) return
              setLessonStatus.mutate({ id: lesson.id, statusId: ReserveStatus.Cancelled })
            },
          },
          // Uhradenú lekciu nemožno vymazať, len zrušiť - platba by visela na
          // symbole bez lekcie (server to odmietne aj pri čiastočnej úhrade).
          ...(paid
            ? []
            : [
                {
                  key: 'delete',
                  label: t('calendar.menuDelete'),
                  className: 'text-brand-600',
                  onSelect: () => {
                    if (window.confirm(t('reservation.deleteConfirm'))) deleteLesson.mutate(lesson.id)
                  },
                },
              ]),
        ],
      })
    }

    return sections
  }

  /**
   * Single click on an instructor. Mirrors the lesson menu: what you can do
   * with the availability block, then its status, then delete.
   */
  function instructorMenu(block: InstructorReservation): MenuSection[] {
    const day = block.start?.slice(0, 10) ?? start
    const statuses: Array<{ id: number; label: string }> = [
      { id: ReserveStatus.Confirmed, label: t('calendar.menuConfirmed') },
      { id: ReserveStatus.Preliminary, label: t('calendar.menuPreliminary') },
      { id: ReserveStatus.Cancelled, label: t('calendar.menuCancelled') },
    ]

    const sections: MenuSection[] = [
      {
        items: [
          ...(readOnly
            ? []
            : [
                {
                  key: 'edit',
                  label: t('calendar.menuEdit'),
                  onSelect: () => setInstructorModal({ reservation: block, day }),
                },
              ]),
          {
            key: 'pdf',
            label: t('calendar.menuPdf'),
            onSelect: () =>
              window.open(instructorReservations.pdfUrl(block.id), '_blank', 'noopener'),
          },
          ...(block.instructor_id
            ? [
                {
                  key: 'rewards',
                  label: t('calendar.menuRewards'),
                  onSelect: () => goTo(`/rewards/${block.instructor_id}`),
                },
              ]
            : []),
        ],
      },
    ]

    if (!readOnly) {
      sections.push({
        items: [
          ...statuses.map((s) => ({
            key: `status-${s.id}`,
            label: s.label,
            checked: block.status_id === s.id,
            onSelect: () => setBlockStatus.mutate({ id: block.id, statusId: s.id }),
          })),
          {
            key: 'delete',
            label: t('calendar.menuDelete'),
            className: 'text-brand-600',
            onSelect: () => {
              if (window.confirm(t('reservation.deleteConfirm'))) deleteBlock.mutate(block.id)
            },
          },
        ],
      })
    }

    return sections
  }

  /**
   * Drag & drop. Reuses the same `move` endpoint as the clipboard paste - the
   * main use is dragging a lesson out of the "bez inštruktora" row onto an
   * instructor, but any lesson can be dragged anywhere in the grid.
   */
  function handleDrop(
    lesson: ClientReservation,
    day: string,
    part: number,
    blockId: number | null,
  ) {
    if (readOnly) return
    if (lesson.has_issued_receipt) return
    if (lesson.instructor_reservation_id === blockId) {
      const currentPart = timeToSlot(
        lesson.start,
        data?.grid.start_hour ?? 9,
        data?.grid.interval ?? 0.5,
      )
      if (currentPart === part && lesson.start?.slice(0, 10) === day) return
    }
    moveReservation.mutate({
      id: lesson.id,
      day,
      part,
      instructor_reservation_id: blockId,
      copy: false,
      ...paidFlag(lesson),
    })
  }

  /** Presun zachová dĺžku lekcie, takže cenu nezmení - netreba sa pýtať.
   *  Server pri uhradenej lekcii `confirm_paid` vyžaduje, posiela sa teda sám. */
  function paidFlag(lesson: ClientReservation) {
    return lesson.client_paid === PaymentType.Unpaid ? {} : { confirm_paid: true }
  }

  /** Paste from the clipboard into an empty slot. */
  function handlePaste(day: string, part: number, blockId: number | null) {
    if (!clipboard || readOnly) return
    const moving = clipboard.mode === 'move'
    moveReservation.mutate(
      {
        id: clipboard.reservation.id,
        day,
        part,
        instructor_reservation_id: blockId,
        copy: !moving,
        ...(moving ? paidFlag(clipboard.reservation) : {}),
      },
      { onSettled: () => setClipboard(null) },
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-400">
        <Spinner className="h-6 w-6" />
      </div>
    )
  }

  if (error || !data) {
    return <Alert variant="error">{t('errors.generic')}</Alert>
  }

  return (
    <div className="space-y-3">
      <OnboardingCard />
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button type="button" className="btn-secondary px-2" onClick={() => navigate(-1)}>
            <ChevronLeft size={16} />
          </button>
          <DateInput
            value={start}
            onChange={(value) => value && setParams({ start: value, days: String(days) })}
            className="w-36"
          />
          <button type="button" className="btn-secondary px-2" onClick={() => navigate(1)}>
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() =>
              setParams({
                start: toISODate(days === 7 ? startOfWeek(new Date()) : new Date()),
                days: String(days),
              })
            }
          >
            {t('global.today')}
          </button>
        </div>

        <select
          value={days}
          onChange={(e) => setDays(Number.parseInt(e.target.value, 10))}
          className="input w-auto"
        >
          <option value={1}>{t('global.days', { count: 1 })}</option>
          <option value={4}>{t('global.days', { count: 4 })}</option>
          <option value={7}>{t('global.week')}</option>
        </select>

        <select
          value={effectiveSort}
          onChange={(e) => changeSort(e.target.value as SortMode)}
          className="input w-auto"
          title={t('calendar.sortBy')}
        >
          <option value="courses">{t('calendar.sortBy')}: {t('calendar.sortCourses')}</option>
          <option value="name">{t('calendar.sortBy')}: {t('calendar.sortName')}</option>
          <option value="priority">{t('calendar.sortBy')}: {t('calendar.sortPriority')}</option>
          {multiCenter ? (
            <option value="center">{t('calendar.sortBy')}: {t('calendar.sortCenter')}</option>
          ) : null}
        </select>

        {/* Strediská - len pri viacerých (`sql/040`). */}
        <CenterFilter className="w-56" />

        <div className="ml-auto flex items-center gap-2">
          {isFetching ? <Spinner className="text-slate-400" /> : null}

          {!readOnly ? (
            <>
              <UndoRedoControl />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => fillFavorites.mutate({ day: start, days })}
                disabled={fillFavorites.isPending}
                title={t('calendar.fillFavoritesHint', { count: days })}
              >
                <Sparkles size={16} />
                <span className="hidden md:inline">{t('calendar.fillFavorites')}</span>
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setInstructorModal({ reservation: null, day: start })}
              >
                <CalendarPlus size={16} />
                <span className="hidden md:inline">{t('calendar.newInstructorReservation')}</span>
              </button>
            </>
          ) : null}

          {!viewOnly ? (
            <a
              href={instructorReservations.dayPdfUrl(start)}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary"
              title={t('calendar.dayPdf')}
            >
              <FileDown size={16} />
            </a>
          ) : null}
        </div>
      </div>

      {readOnly ? <Alert variant="info">{t('errors.readOnly')}</Alert> : null}

      {clipboard ? (
        <Alert variant="warning">
          {clipboard.mode === 'copy' ? t('global.copy') : t('global.move')}:{' '}
          <strong>{clipboard.reservation.client?.full_name}</strong> — kliknite na voľné políčko.{' '}
          <button type="button" className="underline" onClick={() => setClipboard(null)}>
            {t('global.cancel')}
          </button>
        </Alert>
      ) : null}

      <CalendarGrid
        data={data}
        blocks={sortedBlocks}
        readOnly={readOnly}
        clipboard={clipboard}
        onOpenInstructor={
          viewOnly ? noop : (reservation, day) => setInstructorModal({ reservation, day })
        }
        onOpenClient={
          viewOnly
            ? noop
            : (reservation, day, part, blockId) =>
                setClientModal({ reservation, day, part, blockId })
        }
        onPaste={handlePaste}
        onDropLesson={handleDrop}
        onInstructorMenu={
          viewOnly ? noop : (block, x, y) => setMenu({ kind: 'instructor', block, x, y })
        }
        onLessonMenu={viewOnly ? noop : (lesson, x, y) => setMenu({ kind: 'lesson', lesson, x, y })}
        onLessonDetail={viewOnly ? noop : openLessonDetail}
        onFillFavoritesForDay={(day) => fillFavorites.mutate({ day, days: 1 })}
      />

      {menu ? (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          sections={menu.kind === 'lesson' ? lessonMenu(menu.lesson) : instructorMenu(menu.block)}
          onClose={() => setMenu(null)}
        />
      ) : null}

      {payments ? (
        <PaymentDetailsModal
          reservation={payments}
          clientName={payments.client?.full_name || ''}
          readOnly={readOnly}
          onClose={() => setPayments(null)}
        />
      ) : null}

      {instructorModal ? (
        <InstructorReservationModal
          reservation={instructorModal.reservation}
          day={instructorModal.day}
          codebooks={data}
          onClose={() => setInstructorModal(null)}
        />
      ) : null}

      {clientModal ? (
        <ClientReservationModal
          reservation={clientModal.reservation}
          day={clientModal.day}
          part={clientModal.part}
          blockId={clientModal.blockId}
          grid={data.grid}
          codebooks={data}
          onClose={() => setClientModal(null)}
        />
      ) : null}
    </div>
  )
}
